import React, { MouseEvent, MutableRefObject, useMemo, useRef } from 'react';
import cx from 'classnames';
import { ShowTrigger, Tooltip } from '@vimily/bonjoro-ui-components-v2/Tooltip';
import { Service } from '~/types/models';
import { assetUrl } from '~/helpers';
import { projectsUrl } from '~/App/Modules/Testimonials/util';
import { homeUrl } from '~/App/Modules/Home';
import { useServices } from '~/react-hooks/use-services';
import { useFeatures } from '~/react-hooks/use-features';

export interface ServiceIconImageProps {
  service: Service;
  className?: string;
  active?: boolean;
  tooltipLabel?: React.ReactNode;
}

export interface ImageProps {
  activeImageUrl: string;
  inactiveImageUrl: string;
  name: string;
}

const details: Record<Service, ImageProps> = {
  [Service.VideoMessaging]: {
    activeImageUrl: assetUrl('assets/images/icons/nav/vm-color.svg'),
    inactiveImageUrl: assetUrl('assets/images/icons/nav/vm-plain.svg'),
    name: 'Video messaging',
  },
  [Service.Testimonials]: {
    activeImageUrl: assetUrl('assets/images/icons/nav/tm-color.svg'),
    inactiveImageUrl: assetUrl('assets/images/icons/nav/tm-plain.svg'),
    name: 'Testimonials',
  },
};

export function ServiceIconImage({
  className,
  service = Service.VideoMessaging,
  active = false,
  tooltipLabel,
}: ServiceIconImageProps) {
  const tooltipContainerRef = useRef<HTMLSpanElement>(null);

  const { canAccessVideoMessaging, canAccessTestimonials } = useServices();
  const { shouldHideTestimonials } = useFeatures();

  const isVmService = service === Service.VideoMessaging;
  const isTmService = service === Service.Testimonials;

  const { activeImageUrl, inactiveImageUrl, name } = details[service];

  /**
   * Show default tooltip if active
   * If not, check for current service and if it is enabled to show tooltip not enabled
   * If not, show the Try service for free.
   */
  const tooltip = useMemo(() => {
    if (active) return tooltipLabel;

    if (
      (isVmService && canAccessVideoMessaging) ||
      (isTmService && canAccessTestimonials)
    ) {
      return `${name} not enabled for this user`;
    } else {
      return `Try ${name} for free`;
    }
  }, [
    active,
    canAccessVideoMessaging,
    canAccessTestimonials,
    isVmService,
    isTmService,
  ]);

  /**
   * Add onClick only if the service is not active and the team has no access on the service
   */
  const props = useMemo(() => {
    return {
      onClick: () => {
        if (
          (isVmService && canAccessVideoMessaging) ||
          (isTmService && canAccessTestimonials) ||
          active
        )
          return;

        if (isVmService) {
          window.location.href = homeUrl;
        } else if (isTmService) {
          window.location.href = projectsUrl;
        }
      },
      src: active ? activeImageUrl : inactiveImageUrl,
      className: cx(
        className,
        [
          'w-10 h-10 p-1.5 rounded transition-all cursor-pointer',
          'border border-solid v2-border-neutral-100',
        ],
        {
          'hover:!v2-bg-neutral-50 !v2-border-neutral-300 opacity-[0.4] hover:!opacity-100 hover:!v2-border-neutral-100':
            !active,
          'hover:!v2-bg-blue-50': active && isVmService,
          'hover:!v2-bg-magenta-50': active && isTmService,
        }
      ),
      onMouseOver: (e: MouseEvent<HTMLImageElement>) => {
        if (!active) {
          e.currentTarget.src = activeImageUrl;
        }
      },
      onMouseOut: (e: MouseEvent<HTMLImageElement>) => {
        if (!active) {
          e.currentTarget.src = inactiveImageUrl;
        }
      },
    };
  }, [
    active,
    activeImageUrl,
    inactiveImageUrl,
    canAccessVideoMessaging,
    canAccessTestimonials,
    isVmService,
    isTmService,
  ]);

  if (isTmService && shouldHideTestimonials) return null;

  if (!tooltip) return <img {...props} />;

  return (
    <span ref={tooltipContainerRef}>
      <Tooltip
        align='center'
        touchable={false}
        parentRef={tooltipContainerRef as MutableRefObject<HTMLElement>}
        positions={['bottom']}
        className='!z-[999]'
        bubbleClassName={'!px-2 !py-2.5 !leading-4 text-center'}
        arrowSize={12}
        padding={5}
        maxWidth={132}
        showTrigger={ShowTrigger.Hover}
        content={<span className='text-xs'>{tooltip}</span>}
      >
        <img {...props} />
      </Tooltip>
    </span>
  );
}
